/**
 * @file SubmissionDetails.tsx
 * @description
 * Renders the SubmissionDetails page, which fetches a submission from Supabase by ID
 * and then displays a status-based UI. Now updated to also fetch review_data 
 * for the NEEDS_ACTION flow.
 *
 * Key Features:
 * 1. Fetches the submission record including review_data.
 * 2. If loading, shows a basic spinner or text.
 * 3. If submission not found or unauthorized, shows an error message.
 * 4. Otherwise, switches on submission.status to render the correct subcomponent 
 *    (DraftView, NeedsActionView, ReviewView, DecisionView).
 *
 * @dependencies
 * - React, React Router: for routing and state
 * - Supabase client: for database calls
 *
 * @notes
 * - Part of Step 4: needs to retrieve review_data for the NEEDS_ACTION status UI.
 */

import React, { useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSupabase } from '../contexts/SupabaseContext';
import UserNavBar from '../components/UserNavBar';
import DraftView from '../components/submissions/DraftView';
import NeedsActionView from '../components/submissions/NeedsActionView';
import ReviewView from '../components/submissions/ReviewView';
import DecisionView from '../components/submissions/DecisionView';
import { useAuth } from '../hooks/useAuth';
import { SubmissionRow } from '../types/submission';
import withIdleTimer from '../hoc/withIdleTimer';

// Generate a unique ID for this component instance
const COMPONENT_ID = `SubmissionDetails_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

/**
 * SubmissionDetails:
 * Main container component to display different sub-views depending 
 * on the submission's status. We fetch "review_data" as well for NEEDS_ACTION.
 */
const SubmissionDetails: React.FC = () => {
  console.debug(`[${COMPONENT_ID}] SubmissionDetails rendering at ${new Date().toISOString()}`);
  
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, signOut, loading: authLoading, sessionExpired } = useAuth();
  
  // Add a ref to track if a fetch is in progress
  const isFetchingRef = useRef(false);
  
  // Log auth state on every render
  console.debug(`[${COMPONENT_ID}] Auth state: { authLoading: ${authLoading}, userId: ${user?.id || 'null'}, sessionExpired: ${sessionExpired} } at ${new Date().toISOString()}`);

  const [submission, setSubmission] = React.useState<SubmissionRow | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  
  // Log component state on every render
  console.debug(`[${COMPONENT_ID}] Component state: { loading: ${loading}, error: ${error ? 'true' : 'false'}, submission: ${submission ? submission.id : 'null'} } at ${new Date().toISOString()}`);

  const supabase = useSupabase();

  // Handle sign out
  const handleSignOut = async () => {
    console.debug(`[${COMPONENT_ID}] handleSignOut called at ${new Date().toISOString()}`);
    await signOut();
    console.debug(`[${COMPONENT_ID}] signOut completed, navigating to /auth`);
    navigate('/signin');
  };

  // Add effect to handle sessionExpired
  React.useEffect(() => {
    console.debug(`[${COMPONENT_ID}] sessionExpired effect triggered: ${sessionExpired} at ${new Date().toISOString()}`);
    if (sessionExpired) {
      console.debug(`[${COMPONENT_ID}] Session expired, navigating to /auth`);
      navigate('/signin');
    }
  }, [sessionExpired, navigate]);

  // Memoize the fetch function to avoid recreating it on each render
  const fetchSubmission = useCallback(async () => {
    // Skip if no ID is provided
    if (!id) {
      console.debug(`[${COMPONENT_ID}] fetchSubmission called with no id, skipping`);
      return;
    }
    
    // Skip if already fetching
    if (isFetchingRef.current) {
      console.debug(`[${COMPONENT_ID}] fetchSubmission already in progress, skipping duplicate call at ${new Date().toISOString()}`);
      return;
    }
    
    const fetchStartTime = new Date().toISOString();
    console.debug(`[${COMPONENT_ID}] fetchSubmission started at ${fetchStartTime} for submission id: ${id}`);
    
    isFetchingRef.current = true;
    setLoading(true);
    console.debug(`[${COMPONENT_ID}] Set loading=true at ${new Date().toISOString()}`);
    
    setError(null);
    console.debug(`[${COMPONENT_ID}] Set error=null at ${new Date().toISOString()}`);

    try {
      console.debug(`[${COMPONENT_ID}] Executing Supabase query for submission id: ${id}`);
      const queryStartTime = performance.now();
      const { data, error } = await supabase
        .from('submissions')
        .select(`
          id,
          user_id,
          status,
          submission_type,
          created_at,
          updated_at,
          data,
          review_data,
          payment_completed,
          ready_for_pi_approval,
          pi_approval,
          pi_approval_status_msg
        `)
        .eq('id', id)
        .single();
      const queryEndTime = performance.now();
      console.debug(`[${COMPONENT_ID}] Supabase query completed in ${(queryEndTime - queryStartTime).toFixed(2)}ms`);

      if (error) {
        console.error(`[${COMPONENT_ID}] Error fetching submission: [${error.code}] ${error.message}`, error);
        setError(error.message);
        console.debug(`[${COMPONENT_ID}] Set error state at ${new Date().toISOString()}`);
        setSubmission(null);
        console.debug(`[${COMPONENT_ID}] Set submission=null at ${new Date().toISOString()}`);
      } else if (!data) {
        console.warn(`[${COMPONENT_ID}] No submission data found for id: ${id}`);
        setError('Submission not found or inaccessible.');
        console.debug(`[${COMPONENT_ID}] Set error state (not found) at ${new Date().toISOString()}`);
        setSubmission(null);
        console.debug(`[${COMPONENT_ID}] Set submission=null at ${new Date().toISOString()}`);
      } else {
        console.debug(`[${COMPONENT_ID}] Submission data retrieved successfully, id: ${data.id}, status: ${data.status}`);
        setSubmission(data as unknown as SubmissionRow);
        console.debug(`[${COMPONENT_ID}] Set submission state at ${new Date().toISOString()}`);
      }
    } catch (err) {
      console.error(`[${COMPONENT_ID}] Unexpected error fetching submission details:`, err);
      setError('Unexpected error while fetching submission.');
      console.debug(`[${COMPONENT_ID}] Set error state (unexpected) at ${new Date().toISOString()}`);
      setSubmission(null);
      console.debug(`[${COMPONENT_ID}] Set submission=null at ${new Date().toISOString()}`);
    } finally {
      setLoading(false);
      console.debug(`[${COMPONENT_ID}] Set loading=false at ${new Date().toISOString()}`);
      
      isFetchingRef.current = false;
      const fetchEndTime = new Date().toISOString();
      console.debug(`[${COMPONENT_ID}] fetchSubmission completed at ${fetchEndTime} (${Date.parse(fetchEndTime) - Date.parse(fetchStartTime)}ms total)`);
    }
  }, [id, supabase]);

  // Update the dependency array to explicitly include only the necessary dependencies
  React.useEffect(() => {
    console.debug(`[${COMPONENT_ID}] Submission fetch effect triggered with id: ${id || 'undefined'}, authLoading: ${authLoading} at ${new Date().toISOString()}`);
    
    // Skip if auth is still loading or no ID provided
    if (authLoading || !id) {
      console.debug(`[${COMPONENT_ID}] Skipping fetch because ${authLoading ? 'authLoading=true' : 'id is missing'}`);
      return;
    }

    console.debug(`[${COMPONENT_ID}] Calling fetchSubmission() at ${new Date().toISOString()}`);
    fetchSubmission();
  }, [id, authLoading, fetchSubmission]);

  console.debug(`[${COMPONENT_ID}] Determining which view to render: authLoading=${authLoading}, loading=${loading}, error=${!!error}, submission=${submission?.status || 'null'}`);
  
  if (authLoading) {
    console.debug(`[${COMPONENT_ID}] Rendering auth loading view at ${new Date().toISOString()}`);
    return (
      <>
        <UserNavBar user={user} onSignOut={handleSignOut} />
        <div className="p-8 mt-16">
          <h1 className="text-xl font-bold mb-4">Verifying your account...</h1>
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#F55D3E]"></div>
            <span className="ml-2 text-gray-500">Please wait</span>
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    console.debug(`[${COMPONENT_ID}] Rendering submission loading view at ${new Date().toISOString()}`);
    return (
      <>
        <UserNavBar user={user} onSignOut={handleSignOut} />
        <div className="p-8 mt-16">
          <h1 className="text-xl font-bold mb-4">Loading submission details...</h1>
          <p className="text-gray-500">Please wait.</p>
        </div>
      </>
    );
  }

  if (error) {
    console.debug(`[${COMPONENT_ID}] Rendering error view for error: ${error} at ${new Date().toISOString()}`);
    return (
      <>
        <UserNavBar user={user} onSignOut={handleSignOut} />
        <div className="p-8 mt-16">
          <h1 className="text-xl font-bold mb-4">Error</h1>
          <p className="text-gray-500">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 px-4 py-2 bg-[#F55D3E] text-white rounded"
          >
            Go back to Dashboard
          </button>
        </div>
      </>
    );
  }

  if (!submission) {
    console.debug(`[${COMPONENT_ID}] Rendering submission not found view at ${new Date().toISOString()}`);
    return (
      <>
        <UserNavBar user={user} onSignOut={handleSignOut} />
        <div className="p-8 mt-16">
          <h1 className="text-xl font-bold mb-4">Submission not found</h1>
          <p className="text-gray-500">
            We couldn't find a submission with the specified ID, or you do not have access to it.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 px-4 py-2 bg-[#F55D3E] text-white rounded"
          >
            Go back to Dashboard
          </button>
        </div>
      </>
    );
  }

  console.debug(`[${COMPONENT_ID}] Rendering submission view for status: ${submission.status} at ${new Date().toISOString()}`);
  
  // Wrap the status-based views with UserNavBar
  return (
    <>
      <UserNavBar user={user} onSignOut={handleSignOut} />
      <div className="bg-white shadow fixed w-full z-10 top-16">
        {/* Status Bar */}
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            {/* Status Indicators */}
            <div className="flex items-center w-full justify-between">
              {/* Initial Submission */}
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                  submission.status === 'DRAFT' 
                    ? 'bg-[#F55D3E] text-white'
                    : 'bg-[#7AF53E] text-white'
                }`}>
                  {submission.status === 'DRAFT' ? '1' : '✓'}
                </div>
                <p className={`text-sm mt-2 ${
                  submission.status === 'DRAFT' ? 'font-bold text-[#F55D3E]' : 'font-medium text-gray-700'
                }`}>Initial Submission</p>
              </div>
              
              {/* Line connector */}
              <div className="h-[2px] flex-grow mx-4 bg-gray-300"></div>
              
              {/* Edits Required - Only shown if NEEDS_ACTION */}
              {submission.status === 'NEEDS_ACTION' && (
                <>
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-[#F55D3E] text-white flex items-center justify-center">
                      !
                    </div>
                    <p className="text-sm mt-1 font-bold text-[#F55D3E]">Edits Required</p>
                  </div>
                  <div className="h-[2px] flex-grow mx-4 bg-gray-300"></div>
                </>
              )}
              
              {/* Under IRB Review */}
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  submission.status === 'IRB_REVIEW' 
                    ? 'bg-[#F55D3E] text-white' // Active
                    : submission.status === 'DECISION_ISSUED' 
                      ? 'bg-[#7AF53E] text-white' // Completed
                      : 'bg-gray-200 text-gray-500' // Inactive
                }`}>
                  {submission.status === 'DECISION_ISSUED' ? '✓' : '2'}
                </div>
                <p className={`text-sm mt-1 ${
                  submission.status === 'IRB_REVIEW' ? 'font-bold text-[#F55D3E]' : 'font-medium text-gray-700'
                }`}>Under IRB Review</p>
              </div>
              
              {/* Line connector */}
              <div className="h-[2px] flex-grow mx-4 bg-gray-300"></div>
              
              {/* IRB Decision Issued */}
              <div className="flex flex-col items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  submission.status === 'DECISION_ISSUED' 
                    ? 'bg-[#7AF53E] text-white' // Completed
                    : 'bg-gray-200 text-gray-500' // Inactive
                }`}>
                  {submission.status === 'DECISION_ISSUED' ? '✓' : '3'}
                </div>
                <p className={`text-sm mt-1 ${
                  submission.status === 'DECISION_ISSUED' ? 'font-bold text-green-600' : 'font-medium text-gray-700'
                }`}>IRB Decision Issued</p>
              </div>
            </div>
          </div>
          
          {/* Protocol Information */}
          <div className="border-t pt-4 pb-2">
            <h3 className="text-lg font-bold text-gray-800 mb-2">
              {submission.data.protocol_title || 'Untitled Protocol'}
            </h3>
            <p className="text-sm text-gray-700 mb-1">
              PI: {submission.data.pi_first_name || ''} {submission.data.pi_last_name || ''}
            </p>
            <p className="text-sm text-gray-700">
              Sponsor: {submission.data.sponsor_name || 'Not specified'}
            </p>
          </div>
        </div>
      </div>

      {/* This empty div provides the appropriate spacing to push content below the fixed header */}
      <div className="h-80"></div>

      {/* Main content with proper container alignment */}
      <div className="container mx-auto px-6 pb-10 border-t border-gray-200">
        {(() => {
          const viewType = submission.status;
          console.debug(`[${COMPONENT_ID}] Rendering view component for status: ${viewType}`);
          
          switch (viewType) {
            case 'DRAFT':
              return <DraftView submission={submission} />;
            case 'NEEDS_ACTION':
              return <NeedsActionView submission={submission} />;
            case 'IRB_REVIEW':
              return <ReviewView submission={submission} />;
            case 'DECISION_ISSUED':
              return <DecisionView submission={submission} />;
            default:
              console.warn(`[${COMPONENT_ID}] Unknown submission status: ${viewType}, defaulting to DecisionView`);
              return <DecisionView submission={submission} />;
          }
        })()}
      </div>
    </>
  );
};

export default withIdleTimer(SubmissionDetails);
